import styled, { keyframes } from 'styled-components'
import { TTextAlign } from 'types/media'
import { DEFAULT_LINE_HEIGHT, DEFAULT_TEXT_ALIGN } from 'consts/text'

// Two-phase blink: visible f
// or half the period, hidden for the other half.
const cursorBlink = keyframes`
  0%, 50% { opacity: 1; }
  50.01%, 100% { opacity: 0; }
`

export const Cursor = styled.div<{ $color: string }>`
  position: absolute;
  width: 4px;
  pointer-events: none;
  background: ${p => p.$color};
  animation: ${cursorBlink} 1s steps(1, end) infinite;
`

export const SelectionRect = styled.div.attrs<{
  $x: number
  $y: number
  $width: number
  $height: number
}>(({ $x, $y, $width, $height }) => ({
  style: {
    left: `${$x}px`,
    top: `${$y}px`,
    width: `${$width}px`,
    height: `${$height}px`
  }
}))`
  position: absolute;
  pointer-events: none;
  background: rgba(56, 125, 255, 0.35);
`

/**
 * Suppresses the browser's native selection-background rendering on the
 * contentEditable. Without this, native ::selection paints under our custom
 * <SelectionRect> divs and the highlights look doubled / darker. Text is
 * already `color: transparent`, but ::selection re-applies its own color
 * unless we override it here.
 */

export const EditorSurface = styled.div.attrs<{
  $width: number
  $height: number
  $fontPostScriptName: string
  $fontSize: number
  $lineHeight?: number
  $textAlign?: TTextAlign
}>(
  ({
    $width,
    $height,
    $fontPostScriptName,
    $fontSize,
    $lineHeight,
    $textAlign
  }) => ({
    style: {
      width: `${$width}px`,
      // Match the engine bitmap's height so the contentEditable can't extend
      // below the visible engine text — DOM glyphs use different metrics and
      // would otherwise wrap to more lines than the engine, capturing pointer
      // events in a "ghost" area below the rendered text.
      height: `${$height}px`,
      fontFamily: `"${$fontPostScriptName}", sans-serif`,
      fontSize: `${$fontSize}px`,
      lineHeight: String($lineHeight ?? DEFAULT_LINE_HEIGHT),
      textAlign: $textAlign ?? DEFAULT_TEXT_ALIGN
    }
  })
)`
  position: absolute;
  top: 0;
  left: 0;
  box-sizing: border-box;
  /* iOS Safari otherwise intercepts taps as a zoom gesture and the
     synthesized click/dblclick events get swallowed. */
  touch-action: manipulation;
  /* iOS WebKit shows a native "Paste / Select / Select All" callout on tap
     in a contentEditable, and also tries to position the cursor using its
     own DOM-glyph hit-test (which doesn't match our engine bitmap). Both
     of those need to be off — we handle cursor placement ourselves. */
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  // Engine bitmap is the visible surface — keep contentEditable's glyphs
  // invisible. caretColor: transparent hides the browser's own cursor too;
  // we render our own via <Cursor /> using engine metrics so the cursor aligns
  // with the engine glyphs.
  color: transparent;
  caret-color: transparent;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  word-break: normal;
  letter-spacing: 0;
  outline: none;
  background: transparent;
  cursor: text;
  /* iOS Safari/Chrome insist on running their own cursor-positioning and
     edit-menu UI on any touch that hits a contentEditable element — even
     with preventDefault, -webkit-touch-callout, and user-select:none. The
     PointerCatcher sibling intercepts touches BEFORE they reach this
     element; we focus this element programmatically in our handler so the
     soft keyboard still opens. */
  pointer-events: none;
  font-feature-settings: '"kern" 0';
  // Clip any DOM-rendered ghost lines that extend past the engine bitmap so
  // they don't catch pointer events in the area below the visible text.
  overflow: hidden;

  &::selection,
  & *::selection {
    background: transparent;
    color: transparent;
  }
  &::-moz-selection,
  & *::-moz-selection {
    background: transparent;
    color: transparent;
  }
`

/**
 * Transparent overlay that captures every pointer event for the active text
 * editor. The underlying `EditorSurface` (contentEditable) has
 * `pointer-events: none` so iOS WebKit can't run its native cursor + edit-menu
 * gestures on it; we handle hit-testing here and focus the EditorSurface
 * programmatically to open the soft keyboard.
 */
export const PointerCatcher = styled.div.attrs<{
  $width: number
  $height: number
}>(({ $width, $height }) => ({
  style: {
    width: `${$width}px`,
    height: `${$height}px`
  }
}))`
  position: absolute;
  top: 0;
  left: 0;
  background: transparent;
  cursor: text;
  touch-action: manipulation;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
`
